import { useEffect, useMemo, useState } from 'react'
import { listTestGroups, deleteTestGroup, updateTestGroup } from '../api/testGroupApi'
import { appConfirm } from '../../../../components/AppConfirmModal'

export function useTestGroupListScreen() {
  const [statusFilter, setStatusFilter] = useState('Active')
  const [searchText, setSearchText] = useState('')
  const [pageSize, setPageSize] = useState(25)
  const [currentPage, setCurrentPage] = useState(1)

  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isMutating, setIsMutating] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [reloadTick, setReloadTick] = useState(0)

  useEffect(() => {
    let active = true
    setIsLoading(true)
    setError('')

    // Fetch all test groups (archive: 2) so client tab switching is instant
    listTestGroups({ archive: 2 })
      .then((data) => {
        if (!active) return
        setItems(Array.isArray(data) ? data : [])
      })
      .catch((err) => {
        if (!active) return
        setError(err?.message || 'Failed to load test groups')
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [reloadTick])

  const filteredItems = useMemo(() => {
    let result = items

    // Status Filter (Active, Archive, All)
    if (statusFilter === 'Active') {
      result = result.filter((g) => !g.archive)
    } else if (statusFilter === 'Archive') {
      result = result.filter((g) => Boolean(g.archive))
    }

    // Search query
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase()
      result = result.filter((g) => String(g.name || '').toLowerCase().includes(q))
    }

    return result
  }, [items, statusFilter, searchText])

  const totalEntries = filteredItems.length
  const totalPages = Math.max(1, Math.ceil(totalEntries / pageSize))
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredItems.slice(start, start + pageSize)
  }, [filteredItems, currentPage, pageSize])

  const resetFilters = () => {
    setStatusFilter('Active')
    setSearchText('')
    setCurrentPage(1)
  }

  async function handleDelete(id, name) {
    const confirmed = await appConfirm({
      title: 'Delete Test Group',
      message: `Are you sure you want to delete Test Group "${name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!confirmed) return
    setIsMutating(true)
    setError('')
    try {
      await deleteTestGroup(id)
      setSuccess(`Test Group "${name}" deleted successfully.`)
      setReloadTick((t) => t + 1)
    } catch (err) {
      setError(err?.message || 'Failed to delete test group')
    } finally {
      setIsMutating(false)
    }
  }

  async function handleToggleArchive(group) {
    const nextArchive = !group.archive
    const actionLabel = nextArchive ? 'Archive' : 'Restore'
    const confirmed = await appConfirm({
      title: `${actionLabel} Test Group`,
      message: `Are you sure you want to ${actionLabel.toLowerCase()} "${group.name}"?`,
      confirmText: actionLabel,
      variant: nextArchive ? 'warning' : 'primary',
    })
    if (!confirmed) return
    setIsMutating(true)
    setError('')
    try {
      await updateTestGroup(group.id, { name: group.name, archive: nextArchive })
      setSuccess(`Test Group "${group.name}" ${nextArchive ? 'archived' : 'unarchived'} successfully.`)
      setReloadTick((t) => t + 1)
    } catch (err) {
      setError(err?.message || `Failed to ${actionLabel.toLowerCase()} test group`)
    } finally {
      setIsMutating(false)
    }
  }

  return {
    state: {
      items: paginatedItems,
      rawItems: items,
      isLoading,
      isMutating,
      error,
      success,
      totalEntries,
      totalPages,
      currentPage,
      pageSize,
      statusFilter,
      searchText,
    },
    actions: {
      setStatusFilter,
      setSearchText,
      setPageSize,
      setCurrentPage,
      handleDelete,
      handleToggleArchive,
      resetFilters,
      reload: () => setReloadTick((t) => t + 1),
    },
  }
}
